import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, IconButton, Chip,
    TextField, Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { ProductionEntryService, PieceRateOperationService } from '../../services/piece-rate.service';
import { IProductionEntry, IPieceRateOperation } from '../../interfaces/pieceRate.interface';
import { useToast } from '../../context/ToastContext';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import SearchableSelect from '../common/SearchableSelect';
import { EmployeeAutocomplete, EmployeeOption } from '../common/EmployeeAutocomplete';
import { toLocalIsoDate } from '../../utils/dateUtils';

type StatusChipColor = 'success' | 'warning' | 'default';

const statusColor = (status: 'DRAFT' | 'APPROVED'): StatusChipColor =>
    status === 'APPROVED' ? 'success' : 'warning';

const monthRange = (month: string): { from: string; to: string } => {
    const [y, m] = month.split('-').map(Number);
    const from = `${month}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const to = `${month}-${String(lastDay).padStart(2, '0')}`;
    return { from, to };
};

/**
 * Production entry recording + approval (T3a), modelled on the DRAFT/APPROVED lifecycle of
 * ProductionEntryService (T1). Month + employee filter; inline create; per-user bulk-approve
 * (never institution-wide — ApproveRangeRequest.userId is mandatory, R8). Per R3b, an identical
 * (user, date, operation) DRAFT row only WARNS, it never blocks the create.
 */
const ProductionEntryList: React.FC = () => {
    const [selectedEmployee, setSelectedEmployee] = useState<EmployeeOption | null>(null);
    const [month, setMonth] = useState(toLocalIsoDate().slice(0, 7));
    const [entries, setEntries] = useState<IProductionEntry[]>([]);
    const [operations, setOperations] = useState<IPieceRateOperation[]>([]);
    const [workDate, setWorkDate] = useState(toLocalIsoDate());
    const [operationCode, setOperationCode] = useState('');
    const [batchRef, setBatchRef] = useState('');
    const [quantity, setQuantity] = useState<number | ''>('');
    const [remarks, setRemarks] = useState('');
    const { showSuccess, showApiError } = useToast();
    const { institutionId } = usePayrollInstitution();
    const canRecord = usePermission(PERMISSIONS.PRODUCTION_ENTRY_RECORD);
    const canApprove = usePermission(PERMISSIONS.PRODUCTION_ENTRY_APPROVE);

    // Only ACTIVE catalog rows are selectable for a new entry (inactive rows stay visible on the
    // management screen with Deactivate/Activate, but never here).
    const activeOperations = useMemo(() => operations.filter(o => o.isActive), [operations]);

    const loadOperations = async () => {
        if (!institutionId) return;
        try {
            const data = await PieceRateOperationService.getByInstitution(institutionId);
            setOperations(data);
        } catch (error) {
            showApiError(error, 'Failed to load piece-rate operations');
        }
    };

    const loadEntries = async () => {
        if (!institutionId || !selectedEmployee) {
            setEntries([]);
            return;
        }
        try {
            const { from, to } = monthRange(month);
            const data = await ProductionEntryService.listForUser(institutionId, selectedEmployee.id, from, to);
            setEntries(data);
        } catch (error) {
            showApiError(error, 'Failed to load production entries');
        }
    };

    useEffect(() => { loadOperations(); }, [institutionId]);
    useEffect(() => { loadEntries(); }, [institutionId, selectedEmployee, month]);

    // R3b: identical (user, date, operation) DRAFT row already on screen for the values currently
    // entered in the create row -- a warning, never a block. The matched entry (not just a
    // boolean) is kept so its batchRef can be surfaced -- batchRef is the one field that actually
    // disambiguates a legitimate same-day/same-operation split (e.g. two physical batches) from an
    // accidental re-entry, so the warning is useless without it (fix round 1 / H2).
    const duplicateDraftMatch = useMemo(() => {
        if (!workDate || !operationCode) return null;
        return entries.find(e => e.status === 'DRAFT' && e.workDate === workDate && e.operationCode === operationCode) ?? null;
    }, [entries, workDate, operationCode]);

    const monthTotal = useMemo(
        () => entries.filter(e => e.status === 'APPROVED').reduce((sum, e) => sum + e.amount, 0),
        [entries]
    );

    const resetCreateForm = () => {
        setWorkDate(toLocalIsoDate());
        setOperationCode('');
        setBatchRef('');
        setQuantity('');
        setRemarks('');
    };

    const handleCreate = async () => {
        if (!institutionId || !selectedEmployee || !workDate || !operationCode || quantity === '' || quantity <= 0) return;
        try {
            await ProductionEntryService.create({
                institutionId,
                userId: selectedEmployee.id,
                workDate,
                operationCode,
                batchRef: batchRef || undefined,
                quantity: Number(quantity),
                remarks: remarks || undefined,
            });
            showSuccess('Production entry created');
            resetCreateForm();
            loadEntries();
        } catch (error) {
            showApiError(error, 'Failed to create production entry');
        }
    };

    const handleDelete = async (id: string) => {
        if (!institutionId) return;
        try {
            await ProductionEntryService.delete(institutionId, id);
            showSuccess('Production entry deleted');
            loadEntries();
        } catch (error) {
            showApiError(error, 'Failed to delete production entry');
        }
    };

    const handleApprove = async (id: string) => {
        if (!institutionId) return;
        try {
            await ProductionEntryService.approve(institutionId, id);
            showSuccess('Production entry approved');
            loadEntries();
        } catch (error) {
            showApiError(error, 'Failed to approve production entry');
        }
    };

    const handleUnapprove = async (id: string) => {
        if (!institutionId) return;
        try {
            await ProductionEntryService.unapprove(institutionId, id);
            showSuccess('Production entry unapproved');
            loadEntries();
        } catch (error) {
            showApiError(error, 'Failed to unapprove production entry');
        }
    };

    const handleApproveAllInRange = async () => {
        if (!institutionId || !selectedEmployee) return;
        try {
            const { from, to } = monthRange(month);
            await ProductionEntryService.approveRange(institutionId, { userId: selectedEmployee.id, from, to });
            showSuccess('DRAFT production entries in range approved');
            loadEntries();
        } catch (error) {
            showApiError(error, 'Failed to approve production entries');
        }
    };

    const hasDraftInRange = entries.some(e => e.status === 'DRAFT');

    return (
        <Box sx={{ p: 3 }} data-help-page="pieceRate.entries.page">
            <Typography variant="h5" sx={{ mb: 3 }}>Production Entries</Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 3, flexWrap: 'wrap' }} data-help-section="pieceRate.entries.filters">
                <EmployeeAutocomplete
                    label="Employee"
                    data-help="pieceRate.entries.employee"
                    value={selectedEmployee}
                    onChange={setSelectedEmployee}
                    onError={(error) => showApiError(error, 'Failed to search employees')}
                    sx={{ flex: '1 1 260px', maxWidth: 400 }}
                />
                <TextField
                    data-help="pieceRate.entries.month"
                    label="Month"
                    type="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
                {canApprove && (
                    
                    <Button data-help="pieceRate.entries.approveAll"
                        variant="outlined"
                        onClick={handleApproveAllInRange}
                        disabled={!selectedEmployee || !hasDraftInRange}
                    >
                        Approve All DRAFT In Range
                    </Button>
                    
                )}
            </Box>

            {!selectedEmployee && (
                <Alert severity="info" sx={{ mb: 2 }}>Select an employee to view or record production entries.</Alert>
            )}

            {selectedEmployee && entries.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>No production entries found for this employee and month.</Alert>
            )}

            {selectedEmployee && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Approved month total: {monthTotal.toFixed(2)}
                </Typography>
            )}

            {canRecord && selectedEmployee && (
                <Paper sx={{ p: 2, mb: 3 }} data-help-section="pieceRate.entries.record">
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>Record a Production Entry</Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        <TextField
                            data-help="pieceRate.entries.formWorkDate"
                            label="Work Date"
                            type="date"
                            value={workDate}
                            onChange={(e) => setWorkDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                        <SearchableSelect<IPieceRateOperation>
                            options={activeOperations}
                            value={operationCode}
                            onChange={(code) => setOperationCode(code)}
                            getOptionId={op => op.code}
                            getOptionLabel={op => `${op.code} — ${op.displayName}`}
                            label="Operation"
                            data-help="pieceRate.entries.formOperation"
                            size="small"
                            fullWidth={false}
                            sx={{ minWidth: 220 }}
                        />
                        <TextField
                            data-help="pieceRate.entries.formBatchRef"
                            label="Batch Ref"
                            value={batchRef}
                            onChange={(e) => setBatchRef(e.target.value)}
                            size="small"
                        />
                        <TextField
                            data-help="pieceRate.entries.formQuantity"
                            label="Quantity"
                            type="number"
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value === '' ? '' : Number(e.target.value))}
                            size="small"
                            inputProps={{ min: 1, step: 1 }}
                        />
                        <TextField
                            data-help="pieceRate.entries.formRemarks"
                            label="Remarks"
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            size="small"
                        />
                        
                        <Button data-help="pieceRate.entries.addEntry"
                            variant="contained"
                            onClick={handleCreate}
                            disabled={!workDate || !operationCode || quantity === '' || Number(quantity) <= 0}
                        >
                            Add Entry
                        </Button>
                        
                    </Box>

                    {duplicateDraftMatch && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            A DRAFT production entry already exists for this employee, date and operation
                            (batch: {duplicateDraftMatch.batchRef || '—'}).
                            You can still add this one — duplicates are not blocked, only flagged.
                        </Alert>
                    )}
                </Paper>
            )}

            {selectedEmployee && entries.length > 0 && (
                <TableContainer component={Paper} data-help-section="pieceRate.entries.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="pieceRate.entries.col.workDate">Work Date</TableCell>
                                <TableCell data-help="pieceRate.entries.col.operation">Operation</TableCell>
                                <TableCell data-help="pieceRate.entries.col.batchRef">Batch Ref</TableCell>
                                <TableCell align="right" data-help="pieceRate.entries.col.quantity">Quantity</TableCell>
                                <TableCell align="right" data-help="pieceRate.entries.col.rate">Rate</TableCell>
                                <TableCell align="right" data-help="pieceRate.entries.col.amount">Amount</TableCell>
                                <TableCell data-help="pieceRate.entries.col.status">Status</TableCell>
                                <TableCell align="right" data-help="pieceRate.entries.col.actions">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {entries.map((entry) => (
                                <TableRow key={entry.id}>
                                    <TableCell>{entry.workDate}</TableCell>
                                    <TableCell>{entry.operationCode} — {entry.operationDisplayName}</TableCell>
                                    <TableCell>{entry.batchRef || '—'}</TableCell>
                                    <TableCell align="right">{entry.quantity}</TableCell>
                                    <TableCell align="right">{entry.rateApplied.toFixed(2)}</TableCell>
                                    <TableCell align="right">{entry.amount.toFixed(2)}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={entry.status} color={statusColor(entry.status)} />
                                    </TableCell>
                                    <TableCell align="right">
                                        {/* R1: APPROVED rows are frozen wage inputs -- edit/delete only on DRAFT. */}
                                        {entry.status === 'DRAFT' && canRecord && (
                                            <IconButton onClick={() => handleDelete(entry.id)} size="small" color="error" aria-label="delete" data-help="pieceRate.entries.action.delete">
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                        {entry.status === 'DRAFT' && canApprove && (
                                            <Button size="small" data-help="pieceRate.entries.action.approve" onClick={() => handleApprove(entry.id)}>Approve</Button>
                                        )}
                                        {entry.status === 'APPROVED' && canApprove && (
                                            <Button size="small" data-help="pieceRate.entries.action.unapprove" onClick={() => handleUnapprove(entry.id)}>Unapprove</Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default ProductionEntryList;
